import { useEffect, useState } from "react";
import { api, money, formatApiError } from "@/lib/api";
import { Plus, Building2, X } from "lucide-react";
import { toast } from "sonner";

const CLIENT_TYPES = [
    ["particular", "Particular"],
    ["empresa", "Empresa"],
    ["hospital", "Hospital"],
    ["aseguradora", "Aseguradora"],
    ["asistenciadora", "Asistenciadora"],
    ["broker", "Broker"],
    ["colegio", "Colegio"],
    ["residencial", "Residencial"],
    ["obra", "Obra"],
    ["productora", "Productora / Eventos"],
    ["parque_industrial", "Parque Industrial"],
    ["gobierno", "Gobierno"],
];

export default function Clients() {
    const [list, setList] = useState([]);
    const [priceLists, setPriceLists] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        type: "empresa",
        business_name: "",
        commercial_name: "",
        rfc: "",
        contact_name: "",
        phone: "",
        email: "",
        payment_method: "credito",
        credit_days: 30,
        credit_limit: 0,
        price_list_id: "",
        has_agreement: false,
    });

    const load = async () => {
        try {
            const { data } = await api.get("/clients");
            if (data) setList(data);
        } catch (e) { console.warn(e); }
        try {
            const { data } = await api.get("/price-lists");
            setPriceLists(Array.isArray(data) ? data : []);
        } catch (e) { console.warn(e); }
    };

    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditingId(null);
        setForm({ type: "empresa", business_name: "", commercial_name: "", rfc: "", contact_name: "", phone: "", email: "", payment_method: "credito", credit_days: 30, credit_limit: 0, price_list_id: "", has_agreement: false });
        setOpen(true);
    };

    const openEdit = (c) => {
        setEditingId(c.id);
        setForm({
            type: c.type || "empresa",
            business_name: c.business_name || "",
            commercial_name: c.commercial_name || "",
            rfc: c.rfc || "",
            contact_name: c.contact_name || "",
            phone: c.phone || "",
            email: c.email || "",
            payment_method: c.payment_method || "credito",
            credit_days: c.credit_days ?? 30,
            credit_limit: c.credit_limit ?? 0,
            price_list_id: c.price_list_id || "",
            has_agreement: !!c.has_agreement,
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, price_list_id: form.price_list_id || null };
            if (editingId) {
                await api.put(`/clients/${editingId}`, payload);
                toast.success("Cliente actualizado");
            } else {
                await api.post("/clients", payload);
                toast.success("Cliente creado");
            }
            setOpen(false);
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4" data-testid="clients-page">
            <div className="flex justify-between items-center">
                <div className="text-sm text-slate-500">{list.length} cliente(s) registrado(s)</div>
                <button onClick={openNew} className="fm-btn-accent" data-testid="new-client-btn">
                    <Plus className="w-3.5 h-3.5" /> Nuevo cliente
                </button>
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            {["Razón social", "Tipo", "RFC", "Contacto", "Crédito", "Lista de precios", "Convenio", "Servicios", ""].map((h) => (
                                <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((c) => {
                            const pl = priceLists.find((p) => p.id === c.price_list_id);
                            return (
                                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3"><div className="font-medium">{c.business_name}</div><div className="text-[10px] text-slate-500">{c.commercial_name}</div></td>
                                    <td className="py-2 px-3"><span className="fm-badge fm-badge-neutral">{c.type}</span></td>
                                    <td className="py-2 px-3 font-mono text-xs">{c.rfc || "—"}</td>
                                    <td className="py-2 px-3">
                                        <div className="text-xs">{c.contact_name}</div>
                                        <div className="text-[10px] text-slate-500">{c.phone}</div>
                                    </td>
                                    <td className="py-2 px-3 text-xs font-mono">{c.credit_days}d</td>
                                    <td className="py-2 px-3 text-xs">
                                        {pl ? <span className="fm-badge fm-badge-positive">{pl.name}</span> : <span className="text-slate-400">Público</span>}
                                    </td>
                                    <td className="py-2 px-3">{c.has_agreement ? <span className="fm-badge fm-badge-positive">Activo</span> : <span className="fm-badge fm-badge-neutral">Sin convenio</span>}</td>
                                    <td className="py-2 px-3 font-mono text-xs tabular-nums">{c.total_services || 0}</td>
                                    <td className="py-2 px-3 text-right">
                                        <button onClick={() => openEdit(c)} className="text-xs text-slate-500 hover:text-red-600 underline" data-testid={`edit-client-${c.id}`}>Editar</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {list.length === 0 && (
                    <div className="p-10 text-center text-sm text-slate-500">
                        <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Sin clientes. Crea el primero o carga datos demo en Configuración.
                    </div>
                )}
            </div>

            {/* Modal */}
            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="font-display font-bold text-lg">{editingId ? "Editar cliente" : "Nuevo cliente"}</h2>
                            <button onClick={() => { setOpen(false); setEditingId(null); }}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="fm-label">Tipo *</label>
                                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="fm-input">
                                        {CLIENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="fm-label">RFC</label>
                                    <input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} className="fm-input" />
                                </div>
                                <div className="col-span-2">
                                    <label className="fm-label">Razón social *</label>
                                    <input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="fm-input" data-testid="client-business-name" />
                                </div>
                                <div className="col-span-2">
                                    <label className="fm-label">Nombre comercial</label>
                                    <input value={form.commercial_name} onChange={(e) => setForm({ ...form, commercial_name: e.target.value })} className="fm-input" />
                                </div>
                                <div>
                                    <label className="fm-label">Contacto</label>
                                    <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="fm-input" />
                                </div>
                                <div>
                                    <label className="fm-label">Teléfono</label>
                                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="fm-input" />
                                </div>
                                <div className="col-span-2">
                                    <label className="fm-label">Email</label>
                                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="fm-input" />
                                </div>
                                <div>
                                    <label className="fm-label">Método de pago</label>
                                    <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="fm-input">
                                        <option value="credito">Crédito</option>
                                        <option value="contado">Contado</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="fm-label">Días de crédito</label>
                                    <input type="number" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: parseInt(e.target.value) || 0 })} className="fm-input" />
                                </div>
                                <div className="col-span-2">
                                    <label className="fm-label">Lista de precios asignada</label>
                                    <select
                                        value={form.price_list_id || ""}
                                        onChange={(e) => setForm({ ...form, price_list_id: e.target.value })}
                                        className="fm-input"
                                        data-testid="client-price-list-select"
                                    >
                                        <option value="">— Público (lista por defecto) —</option>
                                        {priceLists.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}{p.is_default ? " (predeterminada)" : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="text-[10px] text-slate-500 mt-1">
                                        Esta lista se aplicará automáticamente cuando cotices un servicio para este cliente.
                                    </div>
                                </div>
                                <label className="col-span-2 flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!form.has_agreement}
                                        onChange={(e) => setForm({ ...form, has_agreement: e.target.checked })}
                                        data-testid="client-has-agreement"
                                    />
                                    Cliente con convenio activo (corporativo / aseguradora / hospital)
                                </label>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="fm-btn-outline">Cancelar</button>
                                <button type="submit" className="fm-btn-accent" data-testid="save-client-btn">{editingId ? "Actualizar" : "Guardar"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
